import styled from '@emotion/styled';
import React, { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import Modal from '~ComponentsImproved/Modal';
import Field from '~ComponentsImproved/RHF/Field';
import { useCurrency, useGiftCards, useI18n } from '~Hooks';
import { Button, Card, Input } from '~Styles';

const StatusText = styled.p`
  text-align: center;
`;

const Heading = styled.h2`
  margin-top: 4px;
`;

type Inputs = {
  email: string;
};

enum COMPONENT_STATUS {
  Idle,
  Loading,
  Success,
  Error,
}

export default function EmailModal({
  open,
  onClose,
  shortcode,
  balance,
}: {
  open: boolean;
  onClose: () => void;
  shortcode: string;
  balance: number;
}) {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const giftCards = useGiftCards();
  const formMethods = useForm<Inputs>();

  const [status, setStatus] = useState<COMPONENT_STATUS>(COMPONENT_STATUS.Idle);
  const [error, setError] = useState<string | null>(null);

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    setStatus(COMPONENT_STATUS.Loading);

    const email = data.email.trim();
    giftCards
      .sendEmail(shortcode, email, currency.format(balance))
      .then(({ data: sendEmailData, errors }) => {
        if (sendEmailData?.emailGiftcard.success) {
          onCompleted();
          return;
        }

        if (errors) {
          onError(errors[0].message);
          return;
        }

        onError('Something went wrong');
      })
      .catch(({ message }) => onError(message));
  };

  // When completed, show a success message then close the modal
  const onCompleted = () => {
    setStatus(COMPONENT_STATUS.Success);
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  // When there is an error, show the error message
  const onError = (errMessage?: string) => {
    setStatus(COMPONENT_STATUS.Error);
    setError(errMessage || '');
  };

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty, isSubmitting, isValid },
  } = formMethods;

  return (
    <Modal isShowing={open} hide={onClose}>
      <Card style={{ width: '360px' }}>
        {status === COMPONENT_STATUS.Success && <StatusText>Success!</StatusText>}
        {status === COMPONENT_STATUS.Error && (
          <>
            <StatusText>{i18n('General.Error')}</StatusText>
            <StatusText>
              {i18n('POS.Message')}: {error}
            </StatusText>
            <Button
              fluid
              backgroundColor="colorBlack"
              color="colorWhite"
              onTouchStart={() => {
                setStatus(COMPONENT_STATUS.Idle);
              }}>
              {i18n('GC.Email.Change')}
            </Button>
          </>
        )}
        {status === COMPONENT_STATUS.Loading && (
          <StatusText>{i18n('GC.Email.Sending')}</StatusText>
        )}

        {status === COMPONENT_STATUS.Idle && (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Heading>{i18n('General.EmailAdress')}</Heading>
              <Field name="email" marginBottom="small">
                <Input
                  fluid
                  type="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder="example@email.com"
                  {...register('email', {
                    required: { value: true, message: i18n('GC.Create.Email.Required') },
                    pattern: {
                      value: /\S+@\S+\.\S+/,
                      message: i18n('GC.Create.Email.Invalid'),
                    },
                  })}
                  error={!!errors.email}
                />
              </Field>
              <Button
                fluid
                backgroundColor="colorBlack"
                color="colorWhite"
                type="submit"
                disabled={!isDirty || !isValid || isSubmitting}>
                {i18n('General.Send')}
              </Button>
            </form>
          </FormProvider>
        )}
      </Card>
    </Modal>
  );
}
